// src/hooks/useLazyThumbnail.ts

import { useEffect, useRef, useState, useCallback } from 'react';
import { S3Service } from '../services/s3Service';
import type { AwsCredentials } from '../models/types';

export interface UseLazyThumbnailResult {
  /** Ref to attach to the element */
  ref: React.RefObject<HTMLDivElement>;
  /** The generated pre-signed thumbnail URL, or null if not yet loaded */
  thumbnailUrl: string | null;
  /** Whether the URL is currently being generated */
  isLoading: boolean;
  /** Whether URL generation failed */
  hasError: boolean;
  /** Retry generating the thumbnail URL */
  retry: () => void;
}

/**
 * Custom hook that generates a pre-signed thumbnail URL when credentials are available.
 * Since we paginate at 50 photos, we load all thumbnails on the current page immediately
 * (no IntersectionObserver needed for 50 items).
 */
export function useLazyThumbnail(
  photoKey: string,
  credentials: AwsCredentials | null
): UseLazyThumbnailResult {
  const ref = useRef<HTMLDivElement>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasGenerated = useRef(false);

  // Generate pre-signed URL as soon as credentials are available
  useEffect(() => {
    if (!credentials || hasGenerated.current) return;

    let cancelled = false;

    async function generateUrl() {
      setIsLoading(true);
      setHasError(false);

      try {
        const s3 = new S3Service(credentials!);
        const url = await s3.generateThumbnailUrl(photoKey);
        if (!cancelled) {
          hasGenerated.current = true;
          setThumbnailUrl(url);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    }

    generateUrl();

    return () => {
      cancelled = true;
    };
  }, [credentials, photoKey]);

  const retry = useCallback(() => {
    if (!credentials) return;

    hasGenerated.current = false;
    setThumbnailUrl(null);
    setHasError(false);
    setIsLoading(true);

    async function generateUrl() {
      try {
        const s3 = new S3Service(credentials!);
        const url = await s3.generateThumbnailUrl(photoKey);
        hasGenerated.current = true;
        setThumbnailUrl(url);
        setIsLoading(false);
      } catch {
        setHasError(true);
        setIsLoading(false);
      }
    }

    generateUrl();
  }, [credentials, photoKey]);

  return { ref, thumbnailUrl, isLoading, hasError, retry };
}
